// src/pages/profile/EditProfilePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Upload,
  X,
  AlertCircle,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Building2,
  Warehouse,
  Eye,
  EyeOff,
  Save,
  Lock,
  Check,
  Shield,
} from 'lucide-react';
import { getProfile, updateProfile, changePassword } from '../../services/api/profile';
import { getImageUrl } from '../../utils/imageUtils';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import styles from './EditProfilePage.module.css';

const EditProfilePage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  const { refreshUser } = useAuth();
  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Profile form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    birthday: '',
    profilePicture: null,
  });
  const [originalData, setOriginalData] = useState({});
  const [profilePreview, setProfilePreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Password form state
  const [pwData, setPwData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [pwErrors, setPwErrors] = useState({});
  const [isChangingPw, setIsChangingPw] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        const user = await getProfile();
        setProfileData(user);

        const data = {
          name: user.name || '',
          phone: user.phone || '',
          address: user.address || '',
          birthday: user.birthday ? user.birthday.split('T')[0] : '',
          profilePicture: null,
        };
        setFormData(data);
        setOriginalData(data);

        if (user.profilePicture) {
          setProfilePreview(getImageUrl(user.profilePicture));
        }
      } catch (err) {
        setFetchError(t('profile.failedToLoadProfile'));
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Track changes
  useEffect(() => {
    const changed = Object.keys(formData).some((key) => {
      if (key === 'profilePicture') return formData[key] !== null;
      return formData[key] !== originalData[key];
    });
    setHasChanges(changed || removePhoto);
  }, [formData, originalData, removePhoto]);

  const validateProfile = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('profile.nameRequired');
    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = t('profile.invalidPhone');
    }
    if (formData.profilePicture) {
      const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowed.includes(formData.profilePicture.type)) {
        newErrors.profilePicture = t('profile.invalidProfilePictureFormat');
      } else if (formData.profilePicture.size > 2 * 1024 * 1024) {
        newErrors.profilePicture = t('profile.profilePictureTooLarge');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!pwData.currentPassword) newErrors.currentPassword = t('profile.currentPasswordRequired');
    if (!pwData.newPassword) newErrors.newPassword = t('profile.newPasswordRequired');
    else if (pwData.newPassword.length < 6) newErrors.newPassword = t('profile.newPasswordMinLength');
    if (!pwData.confirmPassword) newErrors.confirmPassword = t('profile.confirmPasswordRequired');
    else if (pwData.newPassword !== pwData.confirmPassword) newErrors.confirmPassword = t('profile.passwordsDoNotMatch');
    setPwErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, profilePicture: file }));
    setRemovePhoto(false);
    const reader = new FileReader();
    reader.onload = (ev) => setProfilePreview(ev.target.result);
    reader.readAsDataURL(file);
    if (errors.profilePicture) setErrors((prev) => ({ ...prev, profilePicture: '' }));
  };

  const handleRemovePhoto = () => {
    setFormData((prev) => ({ ...prev, profilePicture: null }));
    setProfilePreview(null);
    setRemovePhoto(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;
    if (!hasChanges) {
      error(t('profile.noChangesToSave'));
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const submitData = new FormData();

      if (formData.name !== originalData.name) submitData.append('name', formData.name.trim());
      if (formData.phone !== originalData.phone) submitData.append('phone', formData.phone.trim());
      if (formData.address !== originalData.address) submitData.append('address', formData.address.trim());
      if (formData.birthday !== originalData.birthday) submitData.append('birthday', formData.birthday);
      if (formData.profilePicture) submitData.append('profilePicture', formData.profilePicture);
      if (removePhoto) submitData.append('removeProfilePicture', 'true');

      await updateProfile(submitData);
      await refreshUser();

      // Update original data to reflect saved state
      const newOriginal = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        birthday: formData.birthday,
        profilePicture: null,
      };
      setOriginalData(newOriginal);
      setRemovePhoto(false);
      setFormData((prev) => ({ ...prev, profilePicture: null }));

      success(t('profile.profileUpdatedSuccess'));
    } catch (err) {
      const msg = err.response?.data?.message;
      setErrors({ submit: msg || t('profile.failedToUpdate') });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setIsChangingPw(true);
    setPwErrors({});

    try {
      await changePassword({
        currentPassword: pwData.currentPassword,
        newPassword: pwData.newPassword,
      });
      setPwData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      success(t('profile.passwordChangedSuccess'));
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg && msg.toLowerCase().includes('incorrect')) {
        setPwErrors({ currentPassword: t('profile.currentPasswordRequired') + ' (incorrect)' });
      } else {
        setPwErrors({ submit: msg || t('profile.failedToChangePassword') });
      }
    } finally {
      setIsChangingPw(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div className={styles.headerTitle}>
            <User className={styles.headerIcon} size={24} />
            <div>
              <h1>{t('profile.editProfile')}</h1>
              <p>{t('profile.updateProfileDesc')}</p>
            </div>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <h3>{t('profile.loadingProfile')}</h3>
          <p>{t('profile.loadingProfileDesc')}</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div className={styles.headerTitle}>
            <User className={styles.headerIcon} size={24} />
            <div>
              <h1>{t('profile.editProfile')}</h1>
            </div>
          </div>
        </div>
        <div className={styles.errorState}>
          <AlertCircle size={48} className={styles.errorIcon} />
          <h3>{t('errors.loadingError')}</h3>
          <p>{fetchError}</p>
          <button onClick={() => window.location.reload()} className={styles.retryButton}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerTitle}>
          <User className={styles.headerIcon} size={24} />
          <div>
            <h1>{t('profile.editProfile')}</h1>
            <p>{t('profile.updateProfileDesc')}</p>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* ── Profile Form ── */}
        <form onSubmit={handleProfileSubmit} className={styles.section}>
          {errors.submit && (
            <div className={styles.errorBanner}>
              <AlertCircle size={18} />
              <span>{errors.submit}</span>
            </div>
          )}

          {/* Profile Picture */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>{t('profile.profilePicture')}</h2>
              <p>{t('profile.profilePictureDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.avatarSection}>
                {profilePreview ? (
                  <div className={styles.avatarPreview}>
                    <img src={profilePreview} alt="Profile" className={styles.avatarImg} />
                    <button type="button" className={styles.removeAvatarBtn} onClick={handleRemovePhoto}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.avatarPlaceholder} onClick={() => fileInputRef.current?.click()}>
                    <Upload size={28} className={styles.uploadIcon} />
                    <p>{t('profile.clickToUpload')}</p>
                    <span>{t('profile.profilePictureFormats')}</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleProfilePictureChange}
                  style={{ display: 'none' }}
                />
                {profilePreview && (
                  <button type="button" className={styles.changePhotoBtn} onClick={() => fileInputRef.current?.click()}>
                    <Upload size={14} /> {t('profile.clickToUpload')}
                  </button>
                )}
              </div>
              {errors.profilePicture && <span className={styles.errorText}>{errors.profilePicture}</span>}
            </div>
          </div>

          {/* Personal Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>{t('profile.personalInformation')}</h2>
              <p>{t('profile.personalInformationDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('profile.fullName')} *</label>
                  <div className={styles.inputWrapper}>
                    <User size={16} className={styles.inputIcon} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('profile.fullNamePlaceholder')}
                      className={errors.name ? styles.inputError : ''}
                    />
                  </div>
                  {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label>{t('profile.phoneNumber')}</label>
                  <div className={styles.inputWrapper}>
                    <Phone size={16} className={styles.inputIcon} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('profile.phonePlaceholder')}
                      className={errors.phone ? styles.inputError : ''}
                    />
                  </div>
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('profile.address')}</label>
                  <div className={styles.inputWrapper}>
                    <MapPin size={16} className={styles.inputIcon} />
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t('profile.addressPlaceholder')}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>{t('profile.birthday')}</label>
                  <div className={styles.inputWrapper}>
                    <Calendar size={16} className={styles.inputIcon} />
                    <input
                      type="date"
                      name="birthday"
                      value={formData.birthday}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information (read-only) */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>{t('profile.accountInformation')}</h2>
              <p>{t('profile.accountInformationDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('profile.emailAddress')}</label>
                  <div className={`${styles.inputWrapper} ${styles.readOnly}`}>
                    <Mail size={16} className={styles.inputIcon} />
                    <input type="email" value={profileData?.email || ''} readOnly />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>{t('profile.userRole')}</label>
                  <div className={`${styles.inputWrapper} ${styles.readOnly}`}>
                    <Shield size={16} className={styles.inputIcon} />
                    <input type="text" value={profileData?.UserRole?.name || ''} readOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Resources (read-only) */}
          {((profileData?.shops?.length > 0) || (profileData?.warehouses?.length > 0)) && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>{t('profile.assignedResources')}</h2>
                <p>{t('profile.assignedResourcesDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>{t('profile.assignedShops')}</label>
                    {profileData?.shops?.length > 0 ? (
                      <div className={styles.resourceList}>
                        {profileData.shops.map((shop) => (
                          <div key={shop.id} className={`${styles.resourceItem} ${profileData.defaultShop?.id === shop.id ? styles.defaultItem : ''}`}>
                            <Building2 size={14} />
                            <span>{shop.name}</span>
                            {profileData.defaultShop?.id === shop.id && (
                              <span className={styles.defaultBadge}>{t('profile.defaultShop')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.noResource}>{t('profile.noShopsAssigned')}</p>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>{t('profile.assignedWarehouses')}</label>
                    {profileData?.warehouses?.length > 0 ? (
                      <div className={styles.resourceList}>
                        {profileData.warehouses.map((wh) => (
                          <div key={wh.id} className={`${styles.resourceItem} ${profileData.defaultWarehouse?.id === wh.id ? styles.defaultItem : ''}`}>
                            <Warehouse size={14} />
                            <span>{wh.name}</span>
                            {profileData.defaultWarehouse?.id === wh.id && (
                              <span className={styles.defaultBadge}>{t('profile.defaultWarehouse')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.noResource}>{t('profile.noWarehousesAssigned')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Profile */}
          <div className={styles.formActions}>
            <button
              type="submit"
              className={`${styles.saveBtn} ${!hasChanges ? styles.disabled : ''}`}
              disabled={isLoading || !hasChanges}
            >
              {isLoading ? (
                <div className={styles.btnSpinner}></div>
              ) : (
                <>
                  <Save size={16} />
                  {hasChanges ? t('profile.saveChanges') : t('profile.noChanges')}
                </>
              )}
            </button>
          </div>
        </form>

        {/* ── Change Password ── */}
        <form onSubmit={handlePasswordSubmit} className={styles.section}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>{t('profile.changePassword')}</h2>
              <p>{t('profile.changePasswordDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              {pwErrors.submit && (
                <div className={styles.errorBanner}>
                  <AlertCircle size={18} />
                  <span>{pwErrors.submit}</span>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>{t('profile.currentPassword')} *</label>
                <div className={styles.passwordWrapper}>
                  <Lock size={16} className={styles.inputIcon} />
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={pwData.currentPassword}
                    onChange={(e) => {
                      setPwData((p) => ({ ...p, currentPassword: e.target.value }));
                      if (pwErrors.currentPassword) setPwErrors((p) => ({ ...p, currentPassword: '' }));
                    }}
                    placeholder={t('profile.currentPasswordPlaceholder')}
                    className={pwErrors.currentPassword ? styles.inputError : ''}
                  />
                  <button type="button" className={styles.eyeBtn} onClick={() => setShowCurrentPw(!showCurrentPw)}>
                    {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pwErrors.currentPassword && <span className={styles.errorText}>{pwErrors.currentPassword}</span>}
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('profile.newPassword')} *</label>
                  <div className={styles.passwordWrapper}>
                    <Lock size={16} className={styles.inputIcon} />
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={pwData.newPassword}
                      onChange={(e) => {
                        setPwData((p) => ({ ...p, newPassword: e.target.value }));
                        if (pwErrors.newPassword) setPwErrors((p) => ({ ...p, newPassword: '' }));
                      }}
                      placeholder={t('profile.newPasswordPlaceholder')}
                      className={pwErrors.newPassword ? styles.inputError : ''}
                    />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowNewPw(!showNewPw)}>
                      {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pwErrors.newPassword && <span className={styles.errorText}>{pwErrors.newPassword}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label>{t('profile.confirmNewPassword')} *</label>
                  <div className={styles.passwordWrapper}>
                    <Lock size={16} className={styles.inputIcon} />
                    <input
                      type={showConfirmPw ? 'text' : 'password'}
                      value={pwData.confirmPassword}
                      onChange={(e) => {
                        setPwData((p) => ({ ...p, confirmPassword: e.target.value }));
                        if (pwErrors.confirmPassword) setPwErrors((p) => ({ ...p, confirmPassword: '' }));
                      }}
                      placeholder={t('profile.confirmNewPasswordPlaceholder')}
                      className={pwErrors.confirmPassword ? styles.inputError : ''}
                    />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirmPw(!showConfirmPw)}>
                      {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pwErrors.confirmPassword && <span className={styles.errorText}>{pwErrors.confirmPassword}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={isChangingPw}>
              {isChangingPw ? (
                <div className={styles.btnSpinner}></div>
              ) : (
                <>
                  <Check size={16} />
                  {t('profile.updatePassword')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfilePage;
